import { UIValue } from '../ui/types';
import { YPaymentAmount } from '../y/amount';
import { $api } from '../y/api';
import { QRequest, QResponse } from '../y/data-source';
import { YI18NString, YLanguage } from '../y/i18n';
import { yCopy } from '../y/object';
import { $time, yIntervalTime } from '../y/time';
import { YCountryId, YFlags, YId, YTaggedId, YTimeString } from '../y/types';
import { c_cities, CCityId } from './c-city';
import { CEventFlag, CEventId, CSessionFlag } from './c-event';
import { c_event_tags, CEventTagId } from './c-event-tag';
import { CProducerEmail, CProducerEmailKind, CProducerId, CTicketType } from './c-producer';
import { CVenueId, CVenueSceneId } from './c-venue';
import { EPayoutType } from './e-payout';
import { UMediaLink } from './u-media-link';

export type CSessionId = YTaggedId<'c_session'>;

export interface CSession {
    id: CSessionId;
    event_id: CEventId;
    is_drop_in: boolean;
    is_cancelled: boolean;
    time: YTimeString;
    end_time?: YTimeString;
    sales_end: number;
    managed_by_id?: YId;
    duration: number;
    quantity: number;
    quantity_booked: number;
    quantity_reserved: number;
    quantity_standby: number;
    ticket_type: CTicketType;
    instructions: YI18NString;
    base_price: YPaymentAmount;
    payout_price?: YPaymentAmount;
    tickets_scheduled?: YTimeString;
    flags: YFlags<CSessionFlag>
}

export type CVideoId = YTaggedId<'c_video'>;

export interface CVideo {
    id: CVideoId;
    event_id: YId;
    video_id: YId;
    is_cancelled: boolean;
    is_ppv: boolean;
    date?: YTimeString;
}

export interface CEvent {
    id: CEventId;
    is_published: boolean;
    name: YI18NString;
    producer_id: CProducerId;
    city_id: CCityId;
    venue_id: CVenueId;
    scene_id: CVenueSceneId;
    imported_id?: YId;
    event_tag_ids: CEventTagId[];
    slug: string;
    media_links: UMediaLink[];
    picture_ids: YId[];
    producer_emails: CProducerEmail[];
    flags: YFlags<CEventFlag>;
}

export interface CEventStatisticsEntry {
    ratings_up: number;
    ratings_down: number;
    opens: number;
    reminders: number;
    views: number;
    quantity: number;
    quantity_booked: number;
    quantity_standby: number;
}

export interface CEventAdminListView {
    id: CEventId;
    is_published: boolean;
    // added_by_name: string;
    producer_id: CProducerId;
    country_id: YCountryId;
    city_id: CCityId;
    venue_id: CVenueId;
    scene_id: YId;
    imported_id?: YId;
    slug?: string;
    name: YI18NString;
    picture_ids: YId[];
    created_at: YTimeString;
    published_at?: YTimeString;
    next_session?: YTimeString;
    added_by_name: string;

    statistics: CEventStatisticsEntry;

    has_videos: boolean; //TODO: we don't really have this
}

export interface CSessionUpcomingViewEvent {
    id: YId;
    is_test: boolean;
    is_published: boolean;
    is_active: boolean;
    added_by_id: YId;
    producer_id: YId;
    coproducer_ids: YId[];
    country: YCountryId;
    city_id: YId;
    venue_id: YId;
    event_tag_ids: YId[];
    slug: string;
    name: YI18NString;
    picture_ids: YId[];
    payout_type: EPayoutType;
    flags: YFlags<CEventFlag>
}

export interface CSessionUpcomingView {
    id: YId;
    event_id: YId;
    is_published: boolean;
    is_cancelled: boolean;
    is_drop_in: boolean;
    time: YTimeString;
    end_time?: YTimeString;
    sales_end: number;
    duration: number;
    ticket_type: CTicketType;
    imported_id?: YId;
    managed_by_id?: YId;
    quantity: number;
    quantity_booked: number;
    tickets_sent: boolean;

    event: CSessionUpcomingViewEvent;
}

export interface CSessionEventView extends CSession {
    event: CEvent;
}

export interface CEventAdminView extends CEvent {
    sessions: CSession[];
    videos: CVideo[];
}

export interface CReviewProducerView {
    event_id: YId;
    event_name: string;
    session_time: YTimeString;
    user_name: string;
    text: string;
    rating: 'down' | 'up';
    created_at: YTimeString;
}

export interface CReviewAdminView {
    id: YId;
    event_id: YId;
    city_id: YId;
    producer_id: YId;
    event_name: string;
    session_time: YTimeString;
    user_name: string;
    text: string;
    rating: 'down' | 'up';
    created_at: YTimeString;
    is_hidden: boolean;
}

const tz = 'Europe/Stockholm';

export function cSessionFullTime(session: CSession) {
    return yIntervalTime($time(session.time), session.is_drop_in ? $time(session.end_time) : undefined, tz);
}

export function cSessionsCreate(event: CEventAdminView, sessions: Partial<CSession>[]) {
    return $api.POST<CEventAdminView>(`c/sessions`, { event_id: event.id, sessions }).then(new_event => {
        Object.assign(event, new_event);
        return event;
    });
}

export function cSessionUpdate(event: CEventAdminView, session_id: YId, value: any) {
    return $api.PUT<CEventAdminView>(`c/session/${session_id}`, value).then(new_event => {
        Object.assign(event, new_event);
        return event;
    });
}

export function cSessionsUpdate(event: CEventAdminView, session_ids: YId[], update: any) {
    return $api.POST<CEventAdminView>(`c/sessions/update_many`, { event_id: event.id, session_ids, update }).then(new_event => {
        Object.assign(event, new_event);
        return event;
    });
}

export function cEventsAdminListQuery(params: QRequest) {
    return $api.POST<QResponse<CEventAdminListView>>('c/events/admin_list_view/query', params);
}

export function cEventCreate(value: any) {
    return $api.POST<CEventAdminView>(`c/event`, value)
}

export function cEventCanDelete(event: CEventAdminListView) {
    if(event.is_published) return false;
    if(event.statistics.quantity_booked) return false;

    return true;
}

export function cEventDelete(event_id: YId) {
    return $api.DELETE(`c/event/${event_id}`);
}

export function cEventUpdate(event: CEventAdminView, value: any) {
    return $api.PUT<CEventAdminView>(`c/event/${event.id}`, value).then(new_event => {
        Object.assign(event, new_event);
        return event;
    })
}

export function cEventDuplicate(event: CEventAdminView) {
    const copy = yCopy(event);
    copy.name = {}
    for(const l in event.name) {
        copy.name[l as YLanguage] = 'Copy of ' + event.name[l as YLanguage];
    }
    copy.sessions = [];
    copy.videos = [];
    copy.id = null as any;
    copy.is_published = false;

    return copy;
}

export function cSessionGetEventView(session_id: YId) {
    return $api.GET<CSessionEventView>(`c/session/${session_id}/event_view`);
}

export function cSessionsGetUpcoming(reload?: boolean) {
    return $api.cachedGET<CSessionUpcomingView[]>('c/sessions/upcoming_view', reload)
}

export function cSessionsGetManaged(reload?: boolean) {
    return $api.cachedGET<CSessionEventView[]>('c/sessions/event_view/managed', reload)
}

export function cEventsKeyNameView(reload?: boolean) {
    return $api.cachedGET<UIValue<CEventId>[]>('c/events/key_name_view', reload).then(r => r.sort((a, b) => a.name.localeCompare(b.name)));
}

export function cEventAdminViewGet(event_id: YId) {
    return $api.GET<CEventAdminView>(`c/event/${event_id}/admin_view`);
}

export function cEventsAdminViewByProducer(producer_id: YId) {
    return $api.GET<CEventAdminView[]>(`c/events/admin_view/producer/${producer_id}`);
}

export function cEventsProducerView(producer_id: YId) {
    return $api.GET<CEventAdminView[]>(`c/events/producer_view/${producer_id}`);
}

export function cEventNextSession(event: CEventAdminView) {
    const now = $time.now();
    for(const s of event.sessions) {
        if($time(s.time).after(now))
            return s;
    }

    return;
}

export function cEventGetSession(event: CEventAdminView, session_id: YId) {
    const session = event.sessions.find(s => s.id == session_id);
    if(!session)
        throw 'Session not found';
    return session;
}

export function cEventActiveSessions(event: CEventAdminView) {
    return event.sessions.filter(s => !s.is_cancelled)
}

export function cEventProducerEmailsOfKind(event: CEvent, kind: CProducerEmailKind) {
    return event.producer_emails.filter(email => email.kinds.includes(kind)).map(email => email.email);
}

export function cSessionSalesEnd(session: CSession | CSessionUpcomingView) {
    return $time(session.time).add(-session.sales_end, $time.hour);
}

export function cSessionSalesEnded(session: CSession) {
    const now = $time.now();
    return now.add(session.sales_end, $time.hour).after($time(session.time));
}

export function cSessionSendBookingList(session: YId, email: string) {
    return $api.POST(`session/${session}/send_list`, { email: email });
}

export function cEventGenreTag(event: CEvent) {
    for(const t of event.event_tag_ids) {
        const tag = c_event_tags.maybe(t);
        if(tag?.is_genre)
            return tag;
    }

    return;
}

export function cEventScreenLocation(event: CEvent) {
    return ['event', c_cities.maybe(event.city_id)!.slug, event.slug];
}

export function cEventPlayerLocation(event: CEvent) {
    return ['event', c_cities.maybe(event.city_id)!.slug, event.slug, 'player'];
}

export function cEventPageUrl(event: CEvent) {
    return 'https://abundolive.se/' + cEventScreenLocation(event).join('/');
}

export function cReviewsProducerView(producer_id: YId) {
    return $api.GET<CReviewProducerView[]>(`c/reviews/producer_view/${producer_id}`);
}

export function cReviewsAdminView() {
    return $api.GET<CReviewAdminView[]>(`c/reviews/admin_view`);
}

export function cReviewHide(id: YId) {
    return $api.POST<boolean>(`c/review/${id}/hide`);
}

//TODOURGENT: fix below
export function cVideosCreate(event_id: YId, value: any[]) {
    return $api.POST<CEventAdminView>(`event/${event_id}/videos`, value);
}

export function cVideoDelete(event_id: YId, video_id: YId) {
    return $api.DELETE(`event/${event_id}/video/${video_id}`);
}

export function cVideoUpdate(event_id: YId, video_id: YId, value: any) {
    return $api.PUT<CEventAdminView>(`event/${event_id}/video/${video_id}`, value);
}

export function cSessionCancel(session_id: YId) {
    return $api.DELETE('c/session/' + session_id);
}

export function cSessionDefaultInstructions(ticket_type?: CTicketType): YI18NString {
    if(ticket_type == 'pickup') {
        return {
            sv: 'Efter bokning står du på gästlista. Uppge ditt namn, bokningsnummer och att du bokat med Abundo i biljettkassan på plats.',
            en: 'You will be placed on the guest list. Please bring your ID, and state your name, booking number, and that you have booked with Abundo at the ticket office or entrance.',
            de: 'Nach der Buchung stehst du auf der Gästeliste. Gib an der Tür deinen Namen, deine Buchungsnummer und dass du bei Abundo gebucht hast.'
        };
    } else if(ticket_type == 'email') {
        return {
            sv: 'Efter bokning får du en bokningsbekräftelse, men detta är inte din biljett. Biljett kommer laddas upp på ditt konto under fliken "Mina bokningar" några timmar innan föreställningsstart. Du kommer även få ett mail med dina biljetter bifogade, även det några timmar innan start. Det är ditt ansvar att ha med dig denna biljett för att visa upp på plats, antingen i mobil eller utskriven. Visa även din legitimation. Din bokning och biljett är personlig och kan ej överlåtas.',
            en: 'Your ticket will be uploaded to your account under "My bookings" a few hours prior to show start. You will also receive an email with your ticket attached, also a few hours prior to the start. It is your responsibility to bring this ticket on your phone or in printed form and show it at the venue. Your booking and ticket are personal and non-transferable.',
            de: 'Dein Ticket wird ein paar Stunden vor Showbeginn in deinem Account unter "Meine Buchungen" hochgeladen. Außerdem erhältst du ein paar Stunden vor Beginn der Show eine E-Mail mit deinem Ticket im Anhang. Es liegt in deiner Verantwortung, dieses Ticket auf deinem Handy oder in ausgedruckter Form mitzubringen und am Veranstaltungsort vorzuzeigen. Deine Reservierung und dein Ticket sind persönlich und nicht übertragbar.'
        };
    } else {
        return {};
    }
}
